import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Won-chime preference hook + audio player.
 *
 * Storage: `yadidos.wonChimeEnabled` in localStorage.
 * NOTE (security review): stores ONLY a non-sensitive UI preference (boolean).
 * Server (`/users/me/preferences`) is the source of truth; localStorage is a
 * first-paint cache. Default is OFF — a chime must be an explicit opt-in.
 *
 * The chime itself is synthesised on-demand via the WebAudio API — no external
 * asset, no autoplay concerns, and it inherits the browser's autoplay policy
 * (which is why we lazily create the AudioContext on the first user gesture,
 * not on mount).
 *
 * Sound design: a single brief two-note bell (C6 → E6, sine, ~300 ms with an
 * exponential envelope). Quiet by default (peak gain 0.08). Reads as
 * institutional acknowledgement, not a game notification.
 */
const STORAGE_KEY = "yadidos.wonChimeEnabled";

function _readLocal() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw === "true";
  } catch (_) {
    return false;
  }
}

// AudioContext is expensive and browsers gate its resume() behind a user
// gesture. We lazily create/resume once per session inside `playChime`.
let _ctx = null;
function _getCtx() {
  if (typeof window === "undefined") return null;
  if (!_ctx) {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      _ctx = new AC();
    } catch (_) { return null; }
  }
  if (_ctx.state === "suspended") { _ctx.resume().catch(() => {}); }
  return _ctx;
}

/**
 * Play a single two-note "Won" chime. Safe to call even if the browser's
 * autoplay policy blocks it — the call is a no-op in that case.
 */
export function playWonChime() {
  const ctx = _getCtx();
  if (!ctx) return;
  const now = ctx.currentTime;

  const playNote = (freq, startOffset, dur = 0.32) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    // Envelope: quick attack, gentle exponential decay. Peak gain kept low
    // so the chime is felt, not announced. Two notes overlap slightly for
    // a soft bell character.
    gain.gain.setValueAtTime(0.0001, now + startOffset);
    gain.gain.exponentialRampToValueAtTime(0.08, now + startOffset + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + startOffset + dur);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now + startOffset);
    osc.stop(now + startOffset + dur + 0.02);
  };

  // Two-note interval — C6 (1046.5 Hz) → E6 (1318.5 Hz), major third.
  playNote(1046.5, 0.00);
  playNote(1318.5, 0.09);
}

export function useWonChime() {
  const { user, mergePreferences } = useAuth();
  const [enabled, setEnabledState] = useState(_readLocal);
  const lastServerRef = useRef(null);

  // Hydrate from the server preference when the auth context settles.
  useEffect(() => {
    const s = user?.preferences?.won_chime_enabled;
    if (typeof s !== "boolean") return;
    if (lastServerRef.current === s) return;
    lastServerRef.current = s;
    if (s !== _readLocal()) {
      try { localStorage.setItem(STORAGE_KEY, String(s)); } catch (e) { console.warn("won-chime: localStorage write failed (server hydrate)", e); }
      setEnabledState(s);
    }
  }, [user]);

  const setEnabled = useCallback((next) => {
    const v = Boolean(next);
    try { localStorage.setItem(STORAGE_KEY, String(v)); } catch (e) { console.warn("won-chime: localStorage write failed (user set)", e); }
    setEnabledState(v);
    // Sync ref forward so mergePreferences re-emit doesn't undo the local
    // change on remount. Fixes settings-persistence-on-return.
    lastServerRef.current = v;
    api.put("/users/me/preferences", { won_chime_enabled: v })
      .then(() => mergePreferences?.({ won_chime_enabled: v }))
      .catch((e) => console.warn("won-chime sync failed", e));
  }, [mergePreferences]);

  const syncedToServer = typeof user?.preferences?.won_chime_enabled === "boolean";
  return { enabled, setEnabled, syncedToServer };
}
